import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Clock, MapPin, Zap, CreditCard, Smartphone, Banknote, ChevronDown, ChevronUp, Battery, Shield, Wrench, X, Car, Send, Check, AlertCircle, BookOpen, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { LegalNoticeEn, PrivacyPolicyEn, TermsConditionsEn } from './LegalPages-en';
import { mockDataEn } from '../mock-en';
import useSEO from '../hooks/useSEO';
import { usePageTracking, useAnalytics } from '../hooks/useAnalytics';
import WhatsAppBookingFlow from './WhatsAppBookingFlow';
import EligibilityChecker from './EligibilityChecker';

const ArranqueExpressLandingEn = () => {
  // SEO: Set canonical and hreflang to avoid duplicate content issues
  useSEO({
    title: 'Car Jump Start Barcelona 24/7 - From €69 | ArrancaYa BCN',
    description: 'ArrancaYa BCN - Professional car jump start service for dead battery in Barcelona 24/7. We arrive today, you only pay if it starts. Call 613 076 099!',
    canonical: 'https://arrancaya-bcn.es/en',
    hreflang: {
      'es': 'https://arrancaya-bcn.es/',
      'ca': 'https://arrancaya-bcn.es/cat',
      'en': 'https://arrancaya-bcn.es/en',
      'x-default': 'https://arrancaya-bcn.es/'
    }
  });

  // Analytics: Track page views and events to MongoDB
  usePageTracking('/en');
  const { trackWhatsAppClick, trackPhoneClick, trackFormOpen } = useAnalytics();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(() => {
    if (typeof window !== 'undefined') {
      return !localStorage.getItem('cookiesAccepted');
    }
    return true;
  });
  const [isWhatsAppFormOpen, setIsWhatsAppFormOpen] = useState(false);
  const [isEligibilityOpen, setIsEligibilityOpen] = useState(false);
  const [currentLegalPage, setCurrentLegalPage] = useState(null);

  // Track page load
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'page_view',
      'page_title': 'Car Jump Start Barcelona - English',
      'page_location': window.location.href,
      'event_category': 'engagement',
      'event_label': 'landing_page_view_en'
    });
  }, []);

  const handleWhatsAppClick = () => {
    trackWhatsAppClick('/en', { source: 'hero_button' });
    trackFormOpen('/en', 'whatsapp_booking');
    
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'whatsapp_form_open',
      'event_category': 'engagement',
      'event_label': 'whatsapp_form_en',
      'value': 1
    });
    setIsWhatsAppFormOpen(true);
  };

  const handlePhoneClick = () => {
    trackPhoneClick('/en', { source: 'hero_button' });
    
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'phone_call',
      'event_category': 'engagement',
      'event_label': 'phone_click_en',
      'value': 1
    });
    window.location.href = `tel:${mockDataEn.contacts.whatsapp}`;
  };

  const acceptCookies = () => {
    setShowCookieBanner(false);
    localStorage.setItem('cookiesAccepted', 'true');
    
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'cookie_consent',
      'event_category': 'engagement',
      'event_label': 'cookies_accepted_en',
      'value': 1
    });
  };

  const trackLegalPageView = (pageName) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'legal_page_view',
      'event_category': 'engagement',
      'event_label': pageName,
      'value': 1
    });
  };

  // English translations
  const t = {
    nav: {
      services: "Services",
      prices: "Prices",
      howItWorks: "How it works",
      blog: "Blog",
      contact: "Contact"
    },
    hero: {
      badge: "24/7 Service in Barcelona",
      title: "Car won't start?",
      subtitle: "We jump-start it today",
      description: "Professional home jump-start service with portable battery.",
      descriptionBold: "No tow truck, no waiting, fixed price.",
      points: [
        "Express service in Barcelona",
        "Fixed price: €69 day / €89 night / €99 weekend",
        "You only pay if your car starts"
      ],
      ctaWhatsApp: "Request Jump-Start",
      ctaPhone: "613 076 099",
      languages: "We speak:",
      faster: "Faster",
      fasterSub: "than your insurance",
      fromPrice: "From €69",
      vatIncluded: "VAT included"
    },
    services: {
      title: "Our Service",
      subtitle: "Everything you need to start your car",
      items: [
        { title: "12V Jump-Start", desc: "Professional jump-start with portable battery", icon: Zap },
        { title: "24/7 Service", desc: "Available every day of the year", icon: Clock },
        { title: "Home Service", desc: "We come wherever you are in Barcelona", icon: MapPin },
        { title: "Fixed Price", desc: "No surprises, you know what you pay", icon: CreditCard }
      ]
    },
    pricing: {
      title: "Clear Pricing",
      subtitle: "Fixed prices, no surprises",
      day: { label: "Day Rate", hours: "8:00 AM - 10:00 PM", days: "Monday to Friday" },
      night: { label: "Night Rate", hours: "10:00 PM - 8:00 AM", days: "Nights" },
      weekend: { label: "Weekend", hours: "All day", days: "Saturdays & Sundays" },
      vatIncluded: "VAT included",
      paymentMethods: "Payment methods",
      noStart: "No start, no pay"
    },
    howItWorks: {
      title: "How does it work?",
      subtitle: "3 simple steps",
      steps: mockDataEn.howItWorks
    },
    zones: {
      title: "Service Areas",
      subtitle: "We cover all Barcelona and surroundings",
      supplement: "Supplement",
      noSupplement: "No supplement"
    },
    faq: {
      title: "Frequently Asked Questions",
      items: mockDataEn.faq
    },
    cta: {
      title: "Need to jump-start your car?",
      subtitle: "Contact us now - Fast and professional service in Barcelona"
    },
    footer: {
      description: "Platform connecting you with independent certified technicians for your vehicle jump-start in Barcelona",
      services: "Services",
      contact: "Contact",
      legal: "Legal",
      privacy: "Privacy Policy",
      terms: "Terms & Conditions",
      cookies: "Cookie Policy",
      legalNotice: "Legal Notice",
      rights: "All rights reserved"
    },
    cookies: {
      message: "We use cookies to improve your experience. By continuing to browse, you accept our use of cookies.",
      accept: "Accept"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src={mockDataEn.company.logo} 
                alt="ArrancaYa BCN" 
                className="h-10 md:h-12 w-auto"
              />
            </div>
            
            {/* Navigation Desktop */}
            <nav className="hidden md:flex space-x-8 items-center">
              <a href="#services" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">{t.nav.services}</a>
              <a href="#prices" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">{t.nav.prices}</a>
              <a href="#how-it-works" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">{t.nav.howItWorks}</a>
              <Link to="/blog" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">{t.nav.blog}</Link>
              
              {/* Language Selector */}
              <div className="border-l border-gray-200 pl-6">
                <select 
                  onChange={(e) => window.location.href = e.target.value}
                  value="/en"
                  className="appearance-none bg-gray-100 border-0 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="/">🇪🇸 ES</option>
                  <option value="/cat">🔴 CA</option>
                  <option value="/en">🇬🇧 EN</option>
                </select>
              </div>
              
              {/* CTA Button */}
              <Button 
                onClick={() => setIsWhatsAppFormOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-6 py-2 font-bold"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                {t.nav.contact}
              </Button>
            </nav>
            
            {/* Mobile CTA */}
            <div className="md:hidden">
              <Button 
                onClick={() => setIsWhatsAppFormOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 text-sm font-bold"
              >
                <MessageCircle className="h-4 w-4 mr-1" />
                {t.nav.contact}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-white py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left">
                <div className="inline-flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Zap className="h-4 w-4 mr-2" />
                  {t.hero.badge}
                </div>
                
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-tight mb-6 tracking-tight">
                  {t.hero.title}
                  <span className="block text-red-600">{t.hero.subtitle}</span>
                </h1>
                
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  {t.hero.description}
                  <strong className="text-gray-900"> {t.hero.descriptionBold}</strong>
                </p>
                
                {/* Trust Points */}
                <div className="space-y-3 mb-8">
                  {t.hero.points.map((point, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-gray-700 font-medium">{point}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  <Button 
                    onClick={handleWhatsAppClick}
                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-xl flex items-center justify-center space-x-3 transition-all duration-200 shadow-lg hover:shadow-xl rounded-xl font-bold"
                    data-testid="hero-whatsapp-btn-en"
                  >
                    <MessageCircle className="h-6 w-6" />
                    <span>{t.hero.ctaWhatsApp}</span>
                  </Button>
                  
                  <Button 
                    onClick={handlePhoneClick}
                    variant="outline"
                    className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-6 text-xl flex items-center justify-center space-x-3 transition-all duration-200 rounded-xl font-bold"
                    data-testid="hero-phone-btn-en"
                  >
                    <Phone className="h-6 w-6" />
                    <span>{t.hero.ctaPhone}</span>
                  </Button>
                </div>
                
                {/* Third CTA - Eligibility Check */}
                <div className="mb-6">
                  <Button 
                    onClick={() => setIsEligibilityOpen(true)}
                    variant="ghost"
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 font-medium flex items-center space-x-2"
                    data-testid="hero-eligibility-btn-en"
                  >
                    <Car className="h-5 w-5" />
                    <span>Is my car compatible?</span>
                  </Button>
                </div>
                
                {/* Languages */}
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <span>{t.hero.languages}</span>
                  <span>🇪🇸 Español</span>
                  <span>•</span>
                  <span>🇬🇧 English</span>
                  <span>•</span>
                  <span>🇫🇷 Français</span>
                </div>
              </div>
              
              {/* Right - Image + Trust Badge */}
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={mockDataEn.company.heroImage}
                    alt="ArrancaYa BCN technician performing professional jump-start"
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
                
                {/* Floating Badge */}
                <div className="absolute -bottom-4 left-4 bg-white rounded-xl shadow-xl p-4 border border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Zap className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{t.hero.faster}</p>
                      <p className="text-sm text-gray-500">{t.hero.fasterSub}</p>
                    </div>
                  </div>
                </div>
                
                {/* Second Floating Badge */}
                <div className="absolute -top-4 -right-4 bg-green-600 text-white rounded-xl shadow-xl px-4 py-3">
                  <p className="font-bold text-lg">{t.hero.fromPrice}</p>
                  <p className="text-sm text-green-100">{t.hero.vatIncluded}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Car Brands Scrolling Banner */}
      <section className="py-8 bg-white border-y border-gray-200 overflow-hidden">
        <div className="relative">
          <div className="flex animate-scroll space-x-8 md:space-x-12">
            {[...mockDataEn.carBrands, ...mockDataEn.carBrands, ...mockDataEn.carBrands].map((brand, index) => (
              <div key={index} className="flex items-center space-x-2 text-gray-500 whitespace-nowrap flex-shrink-0">
                <Car className="h-4 w-4 md:h-5 md:w-5" />
                <span className="text-base md:text-lg font-medium">{brand}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t.services.title}</h2>
            <p className="text-xl text-gray-600">{t.services.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {t.services.items.map((service, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <service.icon className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="prices" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t.pricing.title}</h2>
            <p className="text-xl text-gray-600">{t.pricing.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Day Rate */}
            <Card className="border-2 border-gray-200 hover:border-green-500 transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">☀️</span>
                </div>
                <h3 className="font-bold text-xl text-gray-900 mb-2">{t.pricing.day.label}</h3>
                <p className="text-sm text-gray-500 mb-4">{t.pricing.day.hours}<br/>{t.pricing.day.days}</p>
                <p className="text-5xl font-black text-green-600 mb-2">€{mockDataEn.pricing.day.price}</p>
                <p className="text-sm text-gray-500">{t.pricing.vatIncluded}</p>
              </CardContent>
            </Card>
            
            {/* Night Rate */}
            <Card className="border-2 border-gray-200 hover:border-green-500 transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🌙</span>
                </div>
                <h3 className="font-bold text-xl text-gray-900 mb-2">{t.pricing.night.label}</h3>
                <p className="text-sm text-gray-500 mb-4">{t.pricing.night.hours}<br/>{t.pricing.night.days}</p>
                <p className="text-5xl font-black text-green-600 mb-2">€{mockDataEn.pricing.night.price}</p>
                <p className="text-sm text-gray-500">{t.pricing.vatIncluded}</p>
              </CardContent>
            </Card>
            
            {/* Weekend Rate */}
            <Card className="border-2 border-green-500 bg-green-50 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">POPULAR</div>
              <CardContent className="p-8 text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🗓️</span>
                </div>
                <h3 className="font-bold text-xl text-gray-900 mb-2">{t.pricing.weekend.label}</h3>
                <p className="text-sm text-gray-500 mb-4">{t.pricing.weekend.hours}<br/>{t.pricing.weekend.days}</p>
                <p className="text-5xl font-black text-green-600 mb-2">€{mockDataEn.pricing.weekend.price}</p>
                <p className="text-sm text-gray-500">{t.pricing.vatIncluded}</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Payment Methods & Guarantee */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center bg-green-100 text-green-700 px-6 py-3 rounded-full font-medium">
              <Shield className="h-5 w-5 mr-2" />
              {t.pricing.noStart}
            </div>
            <div className="mt-6 flex justify-center space-x-6 text-gray-500">
              <span className="flex items-center"><Smartphone className="h-5 w-5 mr-2" /> Bizum</span>
              <span className="flex items-center"><CreditCard className="h-5 w-5 mr-2" /> Card</span>
              <span className="flex items-center"><Banknote className="h-5 w-5 mr-2" /> Cash</span>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-16 md:py-24 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.howItWorks.title}</h2>
            <p className="text-xl text-gray-400">{t.howItWorks.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {t.howItWorks.steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  {step.step}
                </div>
                <h3 className="font-bold text-xl mb-3">{step.title}</h3>
                <p className="text-gray-400">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Zones */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t.zones.title}</h2>
            <p className="text-xl text-gray-600">{t.zones.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {mockDataEn.zones.map((zone, index) => (
              <Card key={index} className={`border-2 ${zone.supplement > 0 ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-lg text-gray-900">{zone.name}</h3>
                    <MapPin className={`h-5 w-5 ${zone.supplement > 0 ? 'text-orange-500' : 'text-green-500'}`} />
                  </div>
                  <p className="text-gray-600 text-sm mb-3">{zone.description}</p>
                  {zone.supplement > 0 ? (
                    <span className="inline-flex items-center bg-orange-200 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                      +€{zone.supplement} {t.zones.supplement}
                    </span>
                  ) : (
                    <span className="inline-flex items-center bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                      ✓ {t.zones.noSupplement}
                    </span>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t.faq.title}</h2>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {t.faq.items.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-xl border border-gray-200 px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-5">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-5">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.cta.title}</h2>
          <p className="text-xl text-green-100 mb-8">{t.cta.subtitle}</p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              onClick={handleWhatsAppClick}
              className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-xl font-bold rounded-xl"
            >
              <MessageCircle className="h-6 w-6 mr-2" />
              WhatsApp
            </Button>
            <Button 
              onClick={handlePhoneClick}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-xl font-bold rounded-xl"
            >
              <Phone className="h-6 w-6 mr-2" />
              {t.hero.ctaPhone}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <img src={mockDataEn.company.logo} alt="ArrancaYa BCN" className="h-10 mb-4 brightness-0 invert" />
              <p className="text-gray-400">{t.footer.description}</p>
            </div>
            
            <div>
              <h4 className="font-bold text-lg mb-4">{t.footer.services}</h4>
              <ul className="space-y-2 text-gray-400">
                <li>12V Jump-Start</li>
                <li>24/7 Service</li>
                <li>Home Service</li>
                <li>Fixed Price</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-lg mb-4">{t.footer.contact}</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Barcelona City</li>
                <li>24/7 Service</li>
                <li className="pt-2">
                  <Button 
                    onClick={handleWhatsAppClick}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    WhatsApp
                  </Button>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-lg mb-4">{t.footer.legal}</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button onClick={() => { setCurrentLegalPage('privacy'); trackLegalPageView('privacy_en'); }} className="hover:text-white transition-colors">
                    {t.footer.privacy}
                  </button>
                </li>
                <li>
                  <button onClick={() => { setCurrentLegalPage('terms'); trackLegalPageView('terms_en'); }} className="hover:text-white transition-colors">
                    {t.footer.terms}
                  </button>
                </li>
                <li>
                  <button onClick={() => { setCurrentLegalPage('legal'); trackLegalPageView('legal_en'); }} className="hover:text-white transition-colors">
                    {t.footer.legalNotice}
                  </button>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-500">
            <p>© {new Date().getFullYear()} ArrancaYa BCN. {t.footer.rights}</p>
          </div>
        </div>
      </footer>

      {/* Cookie Banner */}
      {showCookieBanner && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 z-50">
          <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-300">{t.cookies.message}</p>
            <div className="flex gap-2">
              <Button onClick={acceptCookies} className="bg-green-600 hover:bg-green-700 text-white">
                {t.cookies.accept}
              </Button>
              <Button onClick={() => setShowCookieBanner(false)} variant="ghost" className="text-gray-400 hover:text-white">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Booking Flow Modal */}
      <WhatsAppBookingFlow 
        isOpen={isWhatsAppFormOpen} 
        onClose={() => setIsWhatsAppFormOpen(false)}
        phoneNumber={mockDataEn.contacts.whatsapp}
        lang="en"
      />

      {/* Eligibility Checker Modal */}
      <EligibilityChecker
        isOpen={isEligibilityOpen}
        onClose={() => setIsEligibilityOpen(false)}
        onRequestService={() => setIsWhatsAppFormOpen(true)}
        lang="en"
      />

      {/* Legal Pages Modals */}
      <Dialog open={currentLegalPage === 'privacy'} onOpenChange={() => setCurrentLegalPage(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <PrivacyPolicyEn />
        </DialogContent>
      </Dialog>
      
      <Dialog open={currentLegalPage === 'terms'} onOpenChange={() => setCurrentLegalPage(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <TermsConditionsEn />
        </DialogContent>
      </Dialog>
      
      <Dialog open={currentLegalPage === 'legal'} onOpenChange={() => setCurrentLegalPage(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <LegalNoticeEn />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArranqueExpressLandingEn;
